import React, { useState, useEffect } from 'react';
import { useSelector, RootStateOrAny, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { OrderTypeNumber } from '../../common/enums/orderTypes.enum';
import { CuoTireTypes, getCuoTireTypeLabel, translateCuoTireTypeName } from '../../common/enums/cuoTireTypes.enum';
import { translateAdditionalInfo } from '../actions/translateAdditionalInfo';
import { translateOrderTypeItemName } from '../../common/enums/orderTypes.enum';
import OrderDetailTxt from './forms/edditOrder/OrderDetailTxt';
import OrderDetailLink from './forms/edditOrder/OrderDetailLink';
import { Formik, Form, Field } from 'formik';
import FormDropDown from '../../common/layout/form/FormDropDown';
import NewFormField from '../../common/layout/form/NewFormField';
import NewFormBtns from '../../common/layout/form/NewFormBtns';
import { divider } from '../styles/newOrderForm.styles';
import * as yup from 'yup';
import {
  Panel,
  DetailsList,
  IColumn,
  SelectionMode,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  CommandBar,
  Selection,
  Text,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  DefaultButton
} from 'office-ui-fabric-react';
import {
  cuoCompactContainer,
  cuoGridFourColumns,
  cuoCompactSection,
  cuoCompactTitle,
  cuoCompactText,
  cuoCompactClientInfo,
  cuoCompactClientLabel,
  cuoLoadingContainer,
  cuoFormContainer,
  cuoFormTitle,
  cuoFormSection
} from '../styles/detailTxt.style';

import { toggleCuoSplitsPanel } from '../../../config/redux/orders/ordersPanel/orderPanelActions';
import { reloadOrders, setSelectedOrders } from '../../../config/redux/orders/ordersActions';
import { getCuoOriginatedOrders, getCuoOrderDetails, updateCuoOrderSplit } from '../../../connectors/orders/connectors';
import { getOrderTypeItems } from '../../../connectors/orderTypes/connectors';





interface CuoPositionMapping {
  id: number;
  cuoPositionId: number;
  allocatedQuantity: number;
  cuoPosition: {
    id: number;
    cuoPositionId: number;
    name: string;
    cuoTireType: CuoTireTypes | null;
    quantity: number;
  };
}

interface CuoSplitOrder {
  id: number;
  number: string;
  cuoOrderNumber: string;
  orderTypeItemId: number;
  orderTypeItemName: string;
  additionalInfo: string;
  currentStatus: string;
  cuoPositionMappings: CuoPositionMapping[];
  // Computed properties for backward compatibility
  tireQuantity: number; // sum of all allocatedQuantity
  cuoTireType: CuoTireTypes | null; // null if mixed, single type if all same
}



const CuoSplitsPanel: React.FC = () => {
  const dispatch = useDispatch();
  const { cuoSplitsPanelOpen } = useSelector((state: RootStateOrAny) => state.orderPanel);
  const { selectedOrders } = useSelector((state: RootStateOrAny) => state.orders);
  const { t } = useTranslation("orders");
  const { t: tAdmin } = useTranslation("admin");
  const cuoPanelNames = t("cuoPanel", { returnObjects: true }) as any;

  const cuoSplitValidation = yup.object().shape({
    orderTypeItemId: yup.number().min(1, cuoPanelNames.errorSelectSubtype),
    additionalInfo: yup.string(),
    cuoPositionMappings: yup.array().of(
      yup.object().shape({
        cuoPositionId: yup.number().required(),
        allocatedQuantity: yup.number().min(0, cuoPanelNames.errorNegativeQuantity)
      })
    ).test('at-least-one-allocation', cuoPanelNames.errorAtLeastOne, function(mappings) {
      return mappings ? mappings.some((mapping: any) => mapping.allocatedQuantity > 0) : false;
    })
  });

  const [orders, setOrders] = useState<CuoSplitOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [orderTypeItems, setOrderTypeItems] = useState<IDropdownOption[]>([]);
  
  const [editForm, setEditForm] = useState({
    orderTypeItemId: 0,
    additionalInfo: '',
    cuoPositionMappings: [] as Array<{
      cuoPositionId: number;
      allocatedQuantity: number;
      cuoPosition: {
        id: number;
        cuoPositionId: number;
        name: string;
        cuoTireType: CuoTireTypes | null;
        quantity: number;
      };
    }>
  });
  const [isEditing, setIsEditing] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<number | null>(null);
  const [originalAdditionalInfo, setOriginalAdditionalInfo] = useState('');
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);


  const [cuoOrderDetails, setCuoOrderDetails] = useState<any>(null);

  const [selection] = useState(() => new Selection({
    onSelectionChanged: () => {
      const selectedItems = selection.getSelection();
      setSelectedSplit(selectedItems.length > 0 ? selectedItems[0] as CuoSplitOrder : null);
    }
  }));
  const [selectedSplit, setSelectedSplit] = useState<CuoSplitOrder | null>(null);

  const cuoOrderId = selectedOrders.length > 0 ? selectedOrders[0].cuoOrderId : null;

  const statusNames = t("orderStatus", { returnObjects: true }) as any;

  // CUO status names come from the CUO system in Polish; only "Do wykonania" is imported (SelectedStatuses=2)
  const translateCuoStatus = (statusName: string): string =>
    statusName === "Do wykonania" ? cuoPanelNames.statusToDo : statusName;

  const translateStatus = (status: string): string => {
    switch (status) {
      case 'Złożone':
        return statusNames.placed;
      case 'Przyjęte':
        return statusNames.accepted;
      case 'Realizowane':
      case 'W trakcie realizacji':
        return statusNames.inRealization;
      case 'Zakończone':
      case 'Zrealizowane':
        return statusNames.realized;
      case 'Anulowane':
        return statusNames.canceled;
      case 'Zafakturowane':
        return statusNames.invoiced;
      default:
        return status;
    }
  };

  useEffect(() => {
    if (cuoSplitsPanelOpen && cuoOrderId) {
      loadCuoOrders();
      loadOrderTypeItems();
      loadCuoOrderDetails();
    }
  }, [cuoSplitsPanelOpen, cuoOrderId]);





  const dismissPanel = () => {
    dispatch(toggleCuoSplitsPanel(false));
    setOrders([]);
    setError(null);
    setSuccess(null);
    setIsEditing(false);
    setEditingOrderId(null);
  };



  const loadCuoOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCuoOriginatedOrders({ CuoOrderId: cuoOrderId! });
      setOrders(response.data || []);
    } catch (err) {
      console.error(cuoPanelNames.errorLoading, err);
      setError(cuoPanelNames.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const loadOrderTypeItems = async () => {
    try {
      const response = await getOrderTypeItems(OrderTypeNumber.Vehicle.toString());
      // data keeps the untranslated name - it is stored on the selected order below
      const options = response.data.data.map((item: any) => ({
        key: item.id,
        text: translateOrderTypeItemName(item.name, tAdmin),
        data: item.name
      }));
      setOrderTypeItems(options);

      if (options.length > 0 && editForm.orderTypeItemId === 0) {
        setEditForm(prev => ({
          ...prev,
          orderTypeItemId: Number(options[0].key)
        }));
      }
    } catch (err) {
      console.error(cuoPanelNames.errorLoadingTypes, err);
    }
  };

  const loadCuoOrderDetails = async () => {
    try {
      const response = await getCuoOrderDetails(cuoOrderId!);
      setCuoOrderDetails(response.data);
    } catch (err) {
      console.error(cuoPanelNames.errorLoadingDetails, err);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'cuoOrderNumber',
      name: cuoPanelNames.cuoNumberCol,
      fieldName: 'cuoOrderNumber',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true
    },
    {
      key: 'orderTypeItemName',
      name: cuoPanelNames.subtype,
      fieldName: 'orderTypeItemName',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: CuoSplitOrder) => translateOrderTypeItemName(item.orderTypeItemName, tAdmin)
    },
    {
      key: 'cuoTireType',
      name: cuoPanelNames.tireType,
      fieldName: 'cuoTireType',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      onRender: (item: CuoSplitOrder) => {
        if (item.cuoTireType === null) {
          return cuoPanelNames.mixed;
        }
        return getCuoTireTypeLabel(item.cuoTireType);
      }
    },
    {
      key: 'tireQuantity',
      name: cuoPanelNames.tireCount,
      fieldName: 'tireQuantity',
      minWidth: 80,
      maxWidth: 100,
      isResizable: true
    },
    {
      key: 'currentStatus',
      name: cuoPanelNames.status,
      fieldName: 'currentStatus',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: CuoSplitOrder) => translateStatus(item.currentStatus)
    },
    {
      key: 'additionalInfo',
      name: cuoPanelNames.additionalInfo,
      fieldName: 'additionalInfo',
      minWidth: 150,
      isResizable: true,
      onRender: (item: CuoSplitOrder) => (
        <span style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {translateAdditionalInfo(item.additionalInfo)}
        </span>
      )
    }
  ];

  const startEditing = (order?: CuoSplitOrder) => {
    if (order) {
      setEditForm({
        orderTypeItemId: order.orderTypeItemId || (orderTypeItems.length > 0 ? Number(orderTypeItems[0].key) : 0),
        additionalInfo: translateAdditionalInfo(order.additionalInfo || ''),
        cuoPositionMappings: order.cuoPositionMappings || []
      });
      setOriginalAdditionalInfo(order.additionalInfo || '');
      setEditingOrderId(order.id);
    } else {
      // For new orders, initialize mappings based on available CUO positions
      const defaultOrderTypeItemId = orderTypeItems.length > 0 ? Number(orderTypeItems[0].key) : 0;
      const initialMappings = cuoOrderDetails && cuoOrderDetails.positions
        ? cuoOrderDetails.positions.map((position: any) => ({
            cuoPositionId: position.cuoPositionId,
            allocatedQuantity: 0,
            cuoPosition: position
          }))
        : [];

      setEditForm({
        orderTypeItemId: defaultOrderTypeItemId,
        additionalInfo: '',
        cuoPositionMappings: initialMappings
      });
      setEditingOrderId(null);
    }
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingOrderId(null);
    // Use first available orderTypeItem or 0 as fallback
    const defaultOrderTypeItemId = orderTypeItems.length > 0 ? Number(orderTypeItems[0].key) : 0;
    setEditForm({
      orderTypeItemId: defaultOrderTypeItemId,
      additionalInfo: '',
      cuoPositionMappings: []
    });
  };

  const saveOrder = async (values: any) => {
    if (values.orderTypeItemId === 0) {
      setError(cuoPanelNames.errorSelectOrderType);
      return;
    }

    const totalQuantity = values.cuoPositionMappings.reduce((sum: number, mapping: any) => sum + mapping.allocatedQuantity, 0);
    if (totalQuantity === 0) {
      setError(cuoPanelNames.errorAtLeastOne);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const isNewOrder = editingOrderId === null;
      const additionalInfo = !isNewOrder && values.additionalInfo === translateAdditionalInfo(originalAdditionalInfo)
        ? originalAdditionalInfo
        : values.additionalInfo;



      // Konwertuj mapowania na nową strukturę backend (tylko te z ilością > 0)
      const mappingUpdates = values.cuoPositionMappings
        .filter((m: any) => m.allocatedQuantity > 0)
        .map((m: any) => ({
          CuoPositionId: m.cuoPositionId,
          AllocatedQuantity: m.allocatedQuantity
        }));

      const mappingAdds = values.cuoPositionMappings
        .filter((m: any) => m.allocatedQuantity > 0)
        .map((m: any) => ({
          CuoPositionId: m.cuoPositionId,
          AllocatedQuantity: m.allocatedQuantity
        }));

      const updateData = {
        CuoOrderId: cuoOrderId!,
        Updates: isNewOrder ? [] : [{
          SodoOrderId: editingOrderId!,
          OrderTypeItemId: values.orderTypeItemId,
          AdditionalInfo: additionalInfo,
          MappingUpdates: mappingUpdates
        }],
        Adds: isNewOrder ? [{
          OrderTypeItemId: values.orderTypeItemId,
          AdditionalInfo: values.additionalInfo,
          MappingAdds: mappingAdds
        }] : [],
        Deletes: []
      };

      await updateCuoOrderSplit(cuoOrderId!, updateData);

      setSuccess(isNewOrder ? cuoPanelNames.successAdded : cuoPanelNames.successUpdated);
      const found = orderTypeItems.find(o => Number(o.key) === values.orderTypeItemId);
      const updatedOrderTypeItemName = found ? String(found.data) : '';
      if (selectedOrders && selectedOrders.length > 0) {
        const updated = {
          ...selectedOrders[0],
          orderTypeItemId: values.orderTypeItemId,
          orderTypeItem: updatedOrderTypeItemName,
        };
        dispatch(setSelectedOrders({ selectedOrders: [updated] }));
      }
      await loadCuoOrderDetails();
      await loadCuoOrders();
    } catch (err) {
      console.error('Error saving order:', err);
      setError(cuoPanelNames.errorSaving);
    } finally {
      setSaving(false);
    }
  };

  const deleteOrder = async (orderId: number) => {
    setOrderToDelete(null);
    try {
      setSaving(true);
      setError(null);

      const updateData = {
        CuoOrderId: cuoOrderId!,
        Updates: [],
        Adds: [],
        Deletes: [orderId]
      };

      await updateCuoOrderSplit(cuoOrderId!, updateData);
      setSuccess(cuoPanelNames.successDeleted);
      dispatch(reloadOrders(true));
    } catch (err) {
      console.error('Error deleting order:', err);
      setError(cuoPanelNames.errorDeleting);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    startEditing();
  };

  const handleEditSplit = () => {
    if (selectedSplit) {
      startEditing(selectedSplit);
    }
  };



  const handleDelete = () => {
    if (selectedSplit) {
      setOrderToDelete(selectedSplit.id);
    }
  };

  const commandBarItems = selectedSplit ? [
    {
      key: 'editSplit',
      text: cuoPanelNames.editSplit,
      iconProps: { iconName: 'BranchMerge' },
      onClick: handleEditSplit
    },
    {
      key: 'delete',
      text: cuoPanelNames.delete,
      iconProps: { iconName: 'Delete' },
      onClick: handleDelete
    }
  ] : [
    {
      key: 'add',
      text: cuoPanelNames.add,
      iconProps: { iconName: 'Add' },
      onClick: handleAdd
    }
  ];



  if (!cuoOrderId) {
    return null;
  }



  return (
    <>
      <Dialog
        hidden={orderToDelete === null}
        onDismiss={() => setOrderToDelete(null)}
        dialogContentProps={{ type: DialogType.normal, subText: cuoPanelNames.confirmDelete, closeButtonAriaLabel: tAdmin("nav.close") as string }}
      >
        <DialogFooter>
          <PrimaryButton onClick={() => orderToDelete !== null && deleteOrder(orderToDelete)} text={cuoPanelNames.delete} />
          <DefaultButton onClick={() => setOrderToDelete(null)} text={tAdmin("nav.cancel") as string} />
        </DialogFooter>
      </Dialog>
      <Panel
        headerText={t("cuoPanel.panelTitle", { number: cuoOrderDetails && cuoOrderDetails.cuoOrderNumber ? cuoOrderDetails.cuoOrderNumber : cuoOrderId }) as string}
        isOpen={cuoSplitsPanelOpen}
        closeButtonAriaLabel={tAdmin("nav.close") as string}
        type={4}
        onDismiss={dismissPanel}
        isLightDismiss={false}
        isHiddenOnDismiss={false}
        styles={{
          main: {
            width: '80vw',
            maxWidth: '1200px',
            zIndex: 1000
          },
          content: {
            padding: '20px'
          }
        }}
        isFooterAtBottom={true}
      >
        <Stack tokens={{ childrenGap: 15 }}>
          {error && (
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
              {error}
            </MessageBar>
          )}

          {success && (
            <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSuccess(null)}>
              {success}
            </MessageBar>
          )}

          {cuoOrderDetails && (
            <div className={cuoCompactContainer}>
              {/* Podstawowe informacje */}
              <div className={cuoGridFourColumns}>
                <OrderDetailTxt
                  title={cuoPanelNames.cuoStatus}
                  value={translateCuoStatus(cuoOrderDetails.statusName) || cuoPanelNames.noData}
                />
                <OrderDetailTxt
                  title={cuoPanelNames.tiresOrdered}
                  value={`${cuoOrderDetails.totalTiresOrdered || 0} ${t("quantityExt")}`}
                />
                <OrderDetailTxt
                  title={cuoPanelNames.tiresRealized}
                  value={`${cuoOrderDetails.totalTiresRealized || 0} ${t("quantityExt")}`}
                />
                {cuoOrderDetails.cuoOrderId && (
                  <OrderDetailLink
                    title={cuoPanelNames.cuoLink}
                    url={`https://app.utylizacjaopon.pl/Order/View/${cuoOrderDetails.cuoOrderId}`}
                  />
                )}
              </div>

        
              {cuoOrderDetails.positions && cuoOrderDetails.positions.length > 0 && (
                <div className={cuoCompactSection}>
                  <div className={cuoCompactTitle}>{cuoPanelNames.positions}</div>
                  {cuoOrderDetails.positions.map((position: any, index: number) => {
                    // For Special tire types (including pneumatyczne), show only ordered quantity
                    const isSpecial = position.name && (
                      position.name.toLowerCase().includes('specjalne') ||
                      position.name.toLowerCase().includes('pneumatyczne')
                    );
                    const displayText = isSpecial
                      ? `${position.quantity} ${t("quantityExt")}`
                      : `${position.quantity} ${t("quantityExt")} (${cuoPanelNames.realized} ${position.quantityRealized})`;

                    return (
                      <div key={index} className={cuoCompactText}>
                        <strong>{translateCuoTireTypeName(position.name)}:</strong> {displayText}
                      </div>
                    );
                  })}
                </div>
              )}
       
              {(cuoOrderDetails.clientName || cuoOrderDetails.firmName || cuoOrderDetails.address) && (
                <div className={cuoCompactClientInfo}>
                  <span className={cuoCompactClientLabel}>{cuoPanelNames.client} </span>
                  {cuoOrderDetails.clientName && (
                    <span>{cuoOrderDetails.clientName}</span>
                  )}
                  {cuoOrderDetails.clientName && cuoOrderDetails.firmName && <span> | </span>}
                  {cuoOrderDetails.firmName && (
                    <span>{cuoPanelNames.requester} {cuoOrderDetails.firmName}</span>
                  )}
                  {(cuoOrderDetails.clientName || cuoOrderDetails.firmName) && cuoOrderDetails.address && <span> | </span>}
                  {cuoOrderDetails.address && (
                    <span>{cuoOrderDetails.address}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Command Bar */}
          <CommandBar items={commandBarItems} />



          {loading ? (
            <div className={cuoLoadingContainer}>
              <Spinner size={SpinnerSize.large} label={cuoPanelNames.loadingOrders} />
            </div>
          ) : (
            <DetailsList
              items={orders}
              columns={columns}
              selection={selection}
              selectionMode={SelectionMode.single}
              selectionPreservedOnEmptyClick={true}
              setKey="set"
              layoutMode={1}
              isHeaderVisible={true}
            />
          )}

          {isEditing && (
            <div className={cuoFormContainer}>
              <Text styles={cuoFormTitle}>
                {editingOrderId ? cuoPanelNames.editOrder : cuoPanelNames.addNewOrder}
              </Text>

              <Formik
                initialValues={{
                  orderTypeItemId: editForm.orderTypeItemId,
                  additionalInfo: editForm.additionalInfo,
                  cuoPositionMappings: editForm.cuoPositionMappings
                }}
                validationSchema={cuoSplitValidation}
                onSubmit={async (values) => {
                  await saveOrder(values);
                }}
                enableReinitialize={true}
              >
                {(formikProps) => {
                  return (
                  <Form>
                    <div className={divider}>
                      <FormDropDown
                        name="orderTypeItemId"
                        headerText={cuoPanelNames.subOrderType}
                        options={orderTypeItems}
                        placeholder={orderTypeItems.length === 0 ? cuoPanelNames.loadingTypes : cuoPanelNames.selectOrderType}
                      />
                    </div>

                    {/* Pozycje CUO do mapowania */}
                    {cuoOrderDetails && cuoOrderDetails.positions && cuoOrderDetails.positions.length > 0 && (
                      <div className={divider}>
                        <Text variant="mediumPlus" style={{ marginBottom: '10px', fontWeight: 600 }}>
                          {cuoPanelNames.allocateTires}
                        </Text>
                        {cuoOrderDetails.positions.map((position: any) => {
                          const existingMapping = formikProps.values.cuoPositionMappings.find(
                            (m: any) => m.cuoPositionId === position.cuoPositionId
                          );
                          const currentValue = existingMapping ? existingMapping.allocatedQuantity : 0;

                          return (
                            <div key={position.cuoPositionId} style={{ marginBottom: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                              <Text variant="medium" style={{ marginBottom: '5px', fontWeight: 500 }}>
                                {translateCuoTireTypeName(position.name)} ({cuoPanelNames.available} {position.quantityAvailable !== undefined ? position.quantityAvailable : position.quantity} {t("quantityExt")})
                              </Text>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500 }}>
                                {cuoPanelNames.quantityToAllocate}
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={currentValue}
                                onChange={(e: any) => {
                                  const newValue = parseInt(e.target.value) || 0;
                                  const updatedMappings = [...formikProps.values.cuoPositionMappings];
                                  const existingIndex = updatedMappings.findIndex(m => m.cuoPositionId === position.cuoPositionId);

                                  if (existingIndex >= 0) {
                                    updatedMappings[existingIndex].allocatedQuantity = newValue;
                                  } else {
                                    updatedMappings.push({
                                      cuoPositionId: position.cuoPositionId,
                                      allocatedQuantity: newValue,
                                      cuoPosition: position
                                    });
                                  }

                                  formikProps.setFieldValue('cuoPositionMappings', updatedMappings);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className={divider}>
                      <NewFormField
                        name="additionalInfo"
                        label={cuoPanelNames.additionalInfo}
                        multiline={true}
                        required={false}
                      />
                    </div>

                    <div className={cuoFormSection}>
                      <NewFormBtns
                        cancelForm={cancelEditing}
                        submitBtnText={saving ? cuoPanelNames.saving : cuoPanelNames.save}
                        disabled={saving}
                      />
                    </div>
                  </Form>
                  );
                }}
              </Formik>
            </div>
          )}
        </Stack>
      </Panel>
    </>
  );
};

export default CuoSplitsPanel;
